/**
 * WCS bounds — Block (INSERT) resolution: recursive block-definition-child walk + the full
 * INSERT transform chain (base-point subtract → MINSERT grid offset → scale → rotate → OCS
 * extrusion → translate to the insertion point) (T1 arm (c)).
 *
 * Split out of `./wcsBounds.ts` for the file-size budget and to keep the recursive
 * block-resolution machinery (the most structurally distinct piece — memoized, cycle-guarded,
 * grid-tiling) in its own module, mirroring `cad-core/src/blockGeometry.ts`'s own split from
 * `bounds.ts`/`leafBounds.ts` for the identical reason (that module's own doc: "so
 * `blockGeometry.ts`'s own per-kind block-DEFINITION-child resolution can reuse the exact same
 * per-kind math without a `bounds.ts` ↔ `blockGeometry.ts` import cycle").
 *
 * TRANSFORM CHAIN (Autodesk DXF reference, INSERT group 10/20/30 insertionPoint + 41/42/43
 * scaleX/Y/Z + 50 rotation + 70/71/44/45 MINSERT array + 210/220/230 extrusion; confirmed
 * against `viewportMembership.ts::transformBoundsByInsert` + three independent renderer sites —
 * see `wcsBoundsMath.ts::minsertGridOffsets2D`'s doc):
 *   1. subtract the block DEFINITION's own base point (group 10/20/30 on the BLOCK record) —
 *      the block's local geometry is authored around that point, not necessarily the origin;
 *   2. add the MINSERT grid-cell offset (block-local, pre-scale space);
 *   3. scale by scaleX/scaleY/scaleZ;
 *   4. rotate by `rotation` (degrees, about the INSERT's own OCS Z axis only — AutoCAD INSERT
 *      rotation is always in-plane);
 *   5. translate to `insertionPoint` (itself an OCS-LOCAL coordinate — INSERT is an OCS entity);
 *   6. transform through the INSERT's OWN `extrusion` (OCS→"parent frame" — for a NESTED insert
 *      this composes into the enclosing block's local frame; for the TOP-LEVEL insert this is
 *      the final OCS→WCS step).
 *
 * `null` is the honest "cannot resolve" result (missing/cyclic block, no `blockDefinitions`
 * context, or a definition with no usable geometry) — NEVER a fabricated insertion-point-only
 * box (this module's whole reason for existing is closing exactly that prior gap — see
 * `wcsBounds.ts`'s module header).
 */

import { ALWAYS_INCLUDE, resolveDefinitionWithCycleGuard } from "./viewportMembership";
import type { AttdefEntity, BlockChildEntity, BlockDefinition, BlockInsertEntity, DimensionStyle, MultileaderEntity, TableEntity } from "./model";
import { tableAsBlockReference } from "./tableBlockReference";
import type { AnnotScaleAttributeContext } from "./annotative";
import {
  computeOcsAxes,
  emptyAabb3,
  extendAabb3,
  guardInsertScale,
  isEmptyAabb3,
  minsertGridOffsets2D,
  ocsToWcs,
  rotateDeg2D,
  splineLeaderControlPoints,
  type Local2,
  type OcsAxes,
  type Wcs3,
  type WcsAabb3,
} from "./wcsBoundsMath";
import { textJustifiedLocalBox } from "./wcsBoundsText";
import {
  annotativeScaleMultiplier,
  arcLocal,
  centeredTextBoxLocal,
  circleLocal,
  dimensionLocal,
  ellipseLocal,
  hatchLocal,
  heavyPolylineLocal,
  lineLocal,
  lwpolylineLocal,
  oleFrameLocal,
  multileaderDoglegEnd,
  multileaderLandingEnd,
  multileaderNodeLastVertex,
  mtextLocal,
  pointLocal,
  radToDeg,
  revisionCloudLocal,
  splineLocal,
  textLocal,
  type LocalPointSet,
} from "./wcsBoundsLeaf";

/**
 * Context every `computeEntityWcsBounds`/`blockInsertWcsBounds` call may supply so a `Block`
 * (INSERT) entity can resolve its referenced definition. Absent ⇒ `null` (honest "unknown
 * extent" — never a fabricated box). Mirrors `cad-core/blockGeometry.ts::BlockLookupContext`.
 *
 * `resolvedBlockPointsCache` (@codex T1 arm(c) round-13 P2 finding, OPTIONAL): without it,
 * `blockInsertWcsBounds` creates a FRESH per-definition memoization map on every call — so
 * calling it once per top-level INSERT (the `entities?fields=entityBounds` page's normal access
 * pattern, one lazy getter per entity) re-walks the SAME referenced block definition's own local
 * geometry from scratch for EVERY instance of it on the page, making the cost scale with
 * `insertCount · definitionSize` instead of `distinctDefinitionCount · definitionSize`. A caller
 * that will compute bounds for MANY entities against the SAME `blockDefinitions` within one
 * logical read (e.g. `state/captureSnapshot.ts`'s per-entity `entityBounds` getters, all sharing
 * one document's block table) should build ONE cache via {@link createBlockBoundsCache} and pass
 * it on every call — the per-block-NAME resolved-point memoization then persists across
 * instances, closing the gap. Purely additive/memoizing (never invalidated mid-use), so sharing
 * it is safe as long as `blockDefinitions` itself does not change between calls (true within one
 * snapshot capture — the whole reason this cache exists). Omitted ⇒ the prior per-call-fresh
 * behavior (still fully correct, just not cross-call-cached).
 */
export interface BlockLookupContext {
  readonly blockDefinitions?: Record<string, BlockDefinition>;
  readonly resolvedBlockPointsCache?: BlockBoundsCache;
  // @codex T1 arm(c) round-22 P2 finding: the document's DIMSTYLE table, name-keyed
  // (`doc.styleTables.dimensionStyles`, keyed by each style's own `.name`), so `dimensionLocal`
  // can resolve a Dimension entity's NAMED style (`entity.styleName`) instead of only ever seeing
  // per-entity `styleOverrides` — see `dimensionLocal`'s own doc for the full gap this closes.
  // Populated once per capture pass by `captureSnapshot.ts` (mirrors `blockDefinitions`' own
  // "built once, shared across every entity's lazy getter" discipline). Absent ⇒ every DIMVAR
  // falls back to AutoCAD's hardcoded default, same as before this fix (never a hard requirement).
  readonly dimensionStyles?: Readonly<Record<string, DimensionStyle>>;
}

/** Opaque per-read memoization handle for {@link BlockLookupContext.resolvedBlockPointsCache} — see its doc. */
export type BlockBoundsCache = Map<string, readonly Wcs3[] | typeof ALWAYS_INCLUDE>;

/** Create a fresh, empty {@link BlockBoundsCache} — one per logical read (e.g. one per `captureSnapshot()` call), never reused across a document mutation. */
export function createBlockBoundsCache(): BlockBoundsCache {
  return new Map();
}

/**
 * Own-property-safe lookup into a plain `Record<string, T>` — `key in record`/`hasOwnProperty`
 * gated, NEVER a bare `record[key]` (@codex T1 arm(c) round-23 P2 finding): a plain-object index
 * silently falls through to an INHERITED `Object.prototype` member (a function, not a `T`) for a
 * key like `"constructor"`/`"toString"`/`"hasOwnProperty"`/`"__proto__"` instead of returning
 * `undefined` — the renderer's own equivalent lookup (`dimensionHelpers.ts::resolveDimensionStyleRow`)
 * uses a real `Map.get`, which has no such prototype-chain hazard at all. Shared, package-local
 * helper (not a type change to `Record<string, T>` — minimizes blast radius per this finding).
 *
 * Exported (@codex T1 arm(c) round-24 P2 finding): `wcsBounds.ts`'s own `ctx.blockDefinitions[...]`
 * index read (the MLEADER block-content-attribute-override lookup feeding
 * `mapBlockAttributeReferences`) has the IDENTICAL hazard — mirrors this module's existing
 * `resolveDimensionStyleFromContext` cross-file export precedent (defined here, imported by
 * `wcsBounds.ts`) rather than re-deriving a second copy.
 */
export function safeRecordLookup<T>(record: Readonly<Record<string, T>>, key: string): T | undefined {
  return Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;
}

/**
 * Resolve a Dimension entity's named DIMSTYLE row from the threaded
 * {@link BlockLookupContext.dimensionStyles} table, falling back to `"Standard"` when the named
 * style is absent from the table — mirrors the RENDERER's own resolution exactly
 * (`apps/web/…/scene/dimensionHelpers.ts::resolveDimensionStyleRow`: `dimStylesByName.get
 * (styleName) ?? dimStylesByName.get("Standard")`), so a Dimension referencing an unknown/deleted
 * style name still falls back to the document's real "Standard" row (which may itself carry
 * non-default DIMVAR values) rather than skipping straight past it to `dimensionLocal`'s own
 * hardcoded literal defaults. Shared by both call sites that resolve a Dimension's style
 * (`wcsBounds.ts`'s top-level dispatch and this module's own block-definition-child dispatch,
 * `childLocalPointSet`) so the fallback rule cannot drift between them.
 *
 * @codex T1 arm(c) round-23 P2 finding: `dimensionStyles` is a plain `Record<string, DimensionStyle>`
 * (built by the capture path as a plain object, not a `Map`) — a bare `dimensionStyles[styleName]`
 * index silently returns an INHERITED `Object.prototype` member (e.g. the `constructor` function)
 * instead of `undefined` whenever `styleName` happens to collide with a prototype key, which would
 * then either throw downstream (a function is not a `DimensionStyle`) or, worse, be silently used
 * as if it were one. `styleName` is a real DXF STYLE-table name (group 2/3) an attacker-adjacent or
 * simply malformed/hand-edited DXF can set to any string, including `"constructor"` — the renderer's
 * OWN lookup (`Map.get`) has no such hazard, so this fix makes the two lookups agree for every input,
 * not just well-behaved ones. {@link safeRecordLookup} closes this for BOTH tiers (the named style
 * AND the `"Standard"` fallback — a document could equally have no `"Standard"` entry at all).
 */
export function resolveDimensionStyleFromContext(
  styleName: string,
  dimensionStyles: Readonly<Record<string, DimensionStyle>> | undefined,
): DimensionStyle | undefined {
  if (!dimensionStyles) return undefined;
  return safeRecordLookup(dimensionStyles, styleName) ?? safeRecordLookup(dimensionStyles, "Standard");
}

/** Transform one OCS-local point through an entity's own `extrusion` field (identity when default/absent). */
function throughOwnOcs(p: Wcs3, extrusion?: { x: number; y: number; z: number }): Wcs3 {
  return ocsToWcs(p, computeOcsAxes(extrusion));
}

/**
 * One ATTRIB's minimal shape this module needs — satisfied by both top-level and nested-Block-child
 * `attributes[]`. Exported (with {@link resolveAttributeContribution}) so `@dxf-ai-editor/cad-core`'s
 * INSERT bounds (`blockGeometry.ts`) union the SAME per-instance ATTRIB extent this module computes
 * — one truth for "where does this INSERT's attribute text land", never a second re-derivation.
 */
export interface AttributeLike {
  readonly tag: string;
  readonly value: string;
  // `| undefined` on every optional field: `@dxf-ai-editor/cad-core` compiles under
  // `exactOptionalPropertyTypes`, and the real `BlockInsertAttribute` (zod `.optional()`) carries
  // explicit `undefined` — the shape must accept it verbatim.
  readonly overrideHeight?: number | undefined;
  readonly overrideRotation?: number | undefined;
  readonly overridePosition?: { x: number; y: number } | undefined;
  // @codex T1 arm(c) round-19 P2 finding (F8): the narrowed shape previously dropped `invisible`
  // (carried by the real `BlockInsertAttribute`), so an ATTRIB hidden by an EATTEDIT per-instance
  // override was still bounded even though `attdefFromEntity.ts::resolveInvisible` renders it as
  // nothing.
  readonly invisible?: boolean | undefined;
  // GitHub Codex review PR #558 round 2 P2 — the EATTEDIT WORLD-placement
  // handedness override (DXF group 71, MIRRTEXT companion to
  // `overrideRotation`). Rendering (`attdefFromEntity.ts::resolvePlacement`)
  // uses `attr.overrideGenerationFlags ?? 0` for a WORLD-placed attribute —
  // NOT the matched ATTDEF's own `generationFlags` — so a full MIRROR can
  // place the visible glyph box on the opposite side of its anchor while
  // this narrowed shape (missing the field entirely) left the bounds calc
  // still reading the ATTDEF's stale flags.
  readonly overrideGenerationFlags?: number | undefined;
}

/**
 * AutoCAD's documented default MTEXT "at least" line pitch — 5/3 × nominal text height —
 * @codex T1 arm(c) round-19 P2 finding (F6): the SAME constant the render path stacks a resolved
 * multiline ATTRIB/ATTDEF value's embedded lines with
 * (`apps/web/src/app/scene3d/adapters/typed/attdefFromEntity.ts::LINE_PITCH_FACTOR`).
 * `packages/drawing-model` cannot import `apps/web` (wrong dependency direction — forbidden by
 * `.dependency-cruiser.cjs`), so this tiny documented constant is re-applied here rather than
 * shared — same dup:check-convergence precedent as `wcsBoundsMath.ts::guardInsertScale`.
 */
const ATTRIBUTE_LINE_PITCH_FACTOR = 5 / 3;

/**
 * Multiline ATTRIB/ATTDEF value box union (@codex T1 arm(c) round-19 P2 finding — F6): the render
 * path (`attdefFromEntity.ts::renderAttributeValueLines`) stacks each embedded-newline line of a
 * resolved attribute value at its own line pitch (`ATTRIBUTE_LINE_PITCH_FACTOR` × the line's own
 * height, offset in the text's OWN rotated local frame — `rotateLineStackOffset`, reproduced here
 * via `rotateDeg2D(0, -distance, rotation)`, which is algebraically identical: for `x=0,
 * y=-distance`, `rotateDeg2D` yields `{x: distance·sin, y: -distance·cos}`, exactly
 * `rotateLineStackOffset`'s formula), rendering N separate TEXT lines rather than one single-line
 * run. This helper mirrors that exactly — splitting on the SAME `/\r\n|\r|\n/` (a literal embedded
 * newline; DXF ATTRIB/ATTDEF carries no `\P` MTEXT-paragraph marker at this model layer, per
 * `attdefFromEntity.ts`'s "Multiline fidelity boundary" — dxf-io stores the resolved value as a
 * flat string), building one `textJustifiedLocalBox` per line at its own stacked anchor and
 * unioning every line's corners. Applied to all THREE `resolveAttributeContribution` regimes
 * (`overridePosition`-set, ATTDEF-matched-template, and orphan block-origin) — every one of them
 * funnels through the SAME `renderAttributeValueLines` on the render side, so a single-line value
 * (`lines.length === 1`) reduces to exactly the previous one-box behaviour (byte-identical).
 */
function multilineAttributeLocalCorners(
  insertionPoint: { x: number; y: number },
  alignmentPoint: { x: number; y: number } | undefined,
  height: number,
  rotation: number,
  value: string,
  widthFactor: number,
  hJustification: number,
  vJustification: number,
  generationFlags: number,
  obliqueAngle: number,
): Local2[] {
  const lines = value.split(/\r\n|\r|\n/);
  const linePitchHeight = height > 0 ? height : 2.5; // matches `attdefFromEntity.ts`'s `positiveOr(safeHeight, 2.5)`.
  // @codex T1 arm(c) round-16 P2 finding — "Keep mirrored multiline bounds in
  // sync with rendering". Bit 4 (UPSIDE_DOWN) reverses the RENDERED
  // line-stack direction (`rotateLineStackOffset`'s own `mirrored` parameter,
  // `attdefFromEntity.ts`/`insertAttributes.ts` — line `i` sits ABOVE line 0
  // for a mirrored run, not below), but this function's own doc claims
  // byte-identity with `rotateLineStackOffset` only for the UNMIRRORED case
  // — the stacking OFFSET direction itself was never flipped here, so a
  // mirrored multiline ATTRIB's later lines were bounded on the WRONG side
  // of line 0, omitting rendered glyphs from window selection / spatial-
  // index culling. `mirrorSign` matches `rotateLineStackOffset`'s own
  // `mirrorSign` exactly (flips the ENTIRE unmirrored {x,y} offset).
  const mirrorSign = (generationFlags & 4) !== 0 ? -1 : 1;
  const corners: Local2[] = [];
  for (let i = 0; i < lines.length; i++) {
    const distance = i * linePitchHeight * ATTRIBUTE_LINE_PITCH_FACTOR;
    const off = rotateDeg2D(0, -distance * mirrorSign, rotation);
    const box = textJustifiedLocalBox({
      insertionPoint: { x: insertionPoint.x + off.x, y: insertionPoint.y + off.y },
      alignmentPoint: alignmentPoint ? { x: alignmentPoint.x + off.x, y: alignmentPoint.y + off.y } : undefined,
      height,
      rotation,
      contents: lines[i] ?? "",
      widthFactor,
      hJustification,
      vJustification,
      generationFlags,
      obliqueAngle,
    });
    corners.push(...box.corners);
  }
  return corners;
}

/**
 * @codex T1 arm(c) round-25 P2 finding (Fix 3) — an ATTDEF's OWN `kind: "attribute"`
 * `annotativeContexts[]` entries (`annotScaleAttributeContextSchema`,
 * `packages/drawing-model/src/annotative.ts` — variant 4, "ATTRIB — `AcDbBlkRefObjectContextData`
 * (attribute-instance variant)"; `attdefEntitySchema.annotativeContexts` — `model.ts` line ~2027 —
 * carries the FULL 10-variant `annotScaleContextSchema` union, so a non-"attribute" entry is
 * schema-legal but not meaningful for THIS entity kind). Confirmed LIVE + REAL by reading
 * `apps/web/…/scene3d/adapters/typed/attdefFromEntity.ts::attdefFromEntity` (calls
 * `resolveTypedAnnotation(attdef.annotativeContexts, ctx)` UNCONDITIONALLY, for BOTH Phase A
 * variable-ATTDEF-matched-by-ATTRIB and Phase B constant-ATTDEF cases) and
 * `useCannoscaleResolver.ts::resolveByContextKind`'s `"attribute"` case (returns
 * `insertion`/`rotation`/`effectiveScale` — the SAME shape `"text"` uses, confirming a matched
 * `kind: "attribute"` context genuinely overrides the rendered anchor/rotation/height exactly like
 * a TEXT annotative context does). The per-instance ATTRIB row (`blockInsertAttributeSchema`)
 * carries NO `annotativeContexts` field of its own — the context list lives ONLY on the ATTDEF
 * template and applies UNIFORMLY regardless of which `attributes[]` row (if any) supplied the
 * rendered value, matching `resolveTypedAnnotation`'s own match-by-`scaleName`-only behavior (no
 * `attribTag` gating at match time either).
 */
function attributeAnnotativeContexts(attdef: AttdefEntity | undefined): AnnotScaleAttributeContext[] {
  if (!attdef) return [];
  // `?? []` — mirrors `wcsBoundsLeaf.ts::textLocal`/`mtextLocal`'s own defensive guard: the schema
  // default is `[]` on a real Zod parse, but a hand-built (non-zod-parsed) fixture/synthetic entity
  // can omit the field entirely.
  return (attdef.annotativeContexts ?? []).filter((c): c is AnnotScaleAttributeContext => c.kind === "attribute");
}

/**
 * Per-`kind: "attribute"`-context local corners for ONE resolved attribute value — mirrors
 * {@link multilineAttributeLocalCorners}'s own multiline stacking exactly, just anchored at the
 * CONTEXT's own `insertion`/`rotation` and scaled by {@link annotativeScaleMultiplier} instead of
 * the master `insertionPoint`/`rotation`/`height` — the SAME "union master box with every
 * context's own box" pattern {@link textLocal}'s round-23 F8 fix already establishes for TEXT (see
 * that function's own doc). `baseHeight` is the ALREADY-RESOLVED height the caller's OWN regime
 * uses (the ATTDEF template's `height`, or an EATTEDIT `overrideHeight`, or the constant-ATTDEF
 * cache's `child.height`) — mirrors `applyAnnotativePlacement`'s `height: placement.height *
 * anno.effectiveScale` (the renderer multiplies the ALREADY-RESOLVED placement height, never
 * re-derives from the ATTDEF template alone).
 */
function attributeContextLocalCorners(
  attdef: AttdefEntity,
  value: string,
  baseHeight: number,
  widthFactor: number,
  hJustification: number,
  vJustification: number,
  generationFlags: number,
  obliqueAngle: number,
): Local2[] {
  const corners: Local2[] = [];
  for (const ctx of attributeAnnotativeContexts(attdef)) {
    const height = baseHeight * annotativeScaleMultiplier(ctx.scaleFactor);
    corners.push(
      ...multilineAttributeLocalCorners(
        ctx.insertion,
        undefined,
        height,
        ctx.rotation,
        value,
        widthFactor,
        hJustification,
        vJustification,
        generationFlags,
        obliqueAngle,
      ),
    );
  }
  return corners;
}

/**
 * An insert-like entity's OWN per-instance ATTRIB contribution, split by transform treatment:
 *   - `liveLocalPoints`: an attribute WITHOUT `overridePosition` follows its ATTDEF template LIVE
 *     (schema invariant) — box corners in the SAME local frame as the block-definition's own
 *     geometry, so the caller feeds them into the SAME `applyFullInsertChain` call.
 *   - `overrideBypassPoints`: an attribute WITH `overridePosition` is placed at that position
 *     WITHOUT re-applying THIS insert's own transform (schema invariant: "places the text there
 *     WITHOUT re-applying the parent INSERT affine") — the caller appends these directly to the
 *     transform chain's OUTPUT, never as an input (after MINSERT-tiling — see
 *     `tileAcrossMinsertCells`). (For a NESTED Block child this bypasses only the nested insert's
 *     own chain; the point is still folded into the ANCESTOR chain(s) above, since it rides in
 *     the point list `childLocalPointSet` returns for its "Block" case.)
 *
 * @codex T1 arm(c) round-6 P2 findings:
 *   - finding-1: an `overridePosition` attribute now gets a FULL text box (from `attr.value` +
 *     the matching ATTDEF's justification/widthFactor/generationFlags/obliqueAngle, when found),
 *     not just the bare anchor point — a repositioned attribute's rendered text can extend well
 *     beyond that single point.
 *   - finding-2: this helper is now called for BOTH the top-level INSERT (`blockInsertWcsBounds`)
 *     AND every NESTED "Block" child (`childLocalPointSet`), so a nested INSERT's own attributes
 *     (baked into the ENCLOSING block definition's authored content — fixed regardless of how many
 *     times the outer block itself is top-level-instantiated, hence safe to resolve through the
 *     SAME memoized per-block-name path) are no longer silently dropped.
 *
 * @codex T1 arm(c) round-10 P2 finding — ORPHAN ATTRIB (tag matches no ATTDEF in `def`, e.g. a
 * legacy/BATTMAN-edited block): the prior code dropped it entirely. The typed renderer's own
 * orphan fallback (`attdefFromEntity.ts::orphanAttributeFromEntity`) still shows it — PLAIN style
 * (`widthFactor:1, hJustification:0, vJustification:0, generationFlags:0, obliqueAngle:0` — no
 * ATTDEF to source justification from), `height: attr.overrideHeight ?? 2.5`, `rotation:
 * attr.overrideRotation ?? 0`, anchored at `overridePosition` (WORLD, bypasses the chain) when
 * set, else the BLOCK ORIGIN `(0,0)` (block-local, rides the full chain — the renderer's
 * documented "block-origin fallback"). Reusing the SAME `attdef?.field ?? plainDefault` pattern
 * for BOTH the matched and orphan `overridePosition`-set cases below (`attdef` is optional in the
 * former's `textJustifiedLocalBox` call) avoids a duplicated code path.
 *
 * @codex T1 arm(c) round-19 P2 finding (F7) — DUPLICATE ATTDEF tags matched by OCCURRENCE, not
 * last-write-wins: a block definition with duplicate variable ATTDEF tags (BATTMAN / hand-edited
 * DXF — authoring does not reject it) gets ONE `attributes[]` row per ATTDEF occurrence, in the
 * SAME order (mirrors the renderer's `attdefFromEntity.ts::findMatchingAttribute` — matches by TAG
 * + 0-based per-tag occurrence index among the block definition's NON-CONSTANT ATTDEF children, in
 * `def.entities` walk order; a CONSTANT ATTDEF is excluded from the occurrence count — it carries
 * no per-instance ATTRIB row, so counting it would shift a later variable ATTDEF's occurrence index
 * past its real matching row, mirroring `blockFromEntity.ts`'s own round-5 FINDING 3). A prior
 * `Map<tag, AttdefEntity>` only ever remembered the LAST same-tag ATTDEF, so every duplicated
 * row's box was anchored there instead of at ITS OWN occurrence's position. A stray same-tag row
 * matching ONLY a constant ATTDEF (never a real occurrence) is silently swallowed — a constant's
 * value is authoritative and has no per-instance placement to bound (mirrors the renderer's
 * `constantAttdefTags` stray-discard, `blockFromEntity.ts` round-5 FINDING 3) — rather than falling
 * through to the orphan (block-origin) fallback, which would fabricate a box the renderer never
 * draws.
 *
 * @codex T1 arm(c) round-21 P2 finding (F3 — architectural): a variable (non-constant) ATTDEF's
 * `defaultValue || tag || "?"` template box is ONLY ever what the render path actually draws when
 * an INSERT has NO matching ATTRIB row for that occurrence at all (`resolveAttributeValue`'s
 * `attr === undefined` branch) — when a real row DOES exist, the render substitutes `attr.value`
 * and never draws the template text. `wcsBoundsBlock.ts`'s CACHED, per-block-definition-NAME
 * `childLocalPointSet`("Attdef") no longer contributes this fallback at all for a variable ATTDEF
 * (moved OUT of that instance-agnostic cache — see its own doc) — this function, which already
 * walks `def?.entities` to build `attdefsByTag`/`constantTags` AND already tracks, via `seenByTag`,
 * exactly how many real `attributes[]` rows each tag consumed (the F7 occurrence counter), is the
 * one place that actually KNOWS whether THIS instance supplied a row for a given ATTDEF occurrence.
 * The second pass below (after the main per-`attr` loop) walks every `attdefsByTag` occurrence at or
 * beyond its tag's consumed count — i.e. every occurrence the main loop never visited — and adds the
 * SAME template-fallback box `wcsBoundsBlock.ts` round-20 used to cache unconditionally, now
 * correctly scoped to only the instances that actually lack that row. A CONSTANT ATTDEF never
 * appears in `attdefsByTag` (excluded above, F7) — it stays exclusively in the CACHED path,
 * unaffected by this second pass.
 *
 * This second pass runs UNCONDITIONALLY for every call site (top-level `blockInsertWcsBounds` AND
 * the NESTED-Block-child call site in `childLocalPointSet`'s `case "Block":`) — an earlier draft of
 * this fix gated it to top-level-only on the theory that a nested child leaving a sub-block's
 * variable ATTDEF tag uncovered is the block AUTHOR's deliberate choice, not the "instance added
 * after the ATTDEF existed" anomaly the fallback exists to cover. That distinction does not survive
 * contact with the renderer: `attdefFromEntity.ts::resolveAttributeValue`'s `attr === undefined ->
 * defaultValue || tag || "?"` fallback fires identically regardless of WHY the row is absent or
 * whether the owning INSERT is nested — a nested INSERT with no ATTRIB row for a variable ATTDEF
 * still renders the template-fallback text, same as a top-level one. Gating this pass to top-level
 * only made a nested block's uncovered-tag case silently regress to reporting NOTHING for that
 * ATTDEF (worse than round-20 — a real, previously-covered regression caught by the pre-existing
 * round-6 finding-2 nested-ATTRIB test), instead of the correct template-fallback box. `def` here is
 * ALWAYS the definition whose OWN `attributes[]`/ATTDEFs are being resolved (the nested block's own
 * definition for the nested call, the outer for the top-level one) — this pass never reaches across
 * a nesting boundary, so there is no risk of resolving the WRONG definition's ATTDEFs either way.
 */
export function resolveAttributeContribution(
  attributes: readonly AttributeLike[],
  def: BlockDefinition | undefined,
): { liveLocalPoints: Wcs3[]; overrideBypassPoints: Wcs3[] } {
  const attdefsByTag = new Map<string, AttdefEntity[]>();
  const constantTags = new Set<string>();
  for (const child of def?.entities ?? []) {
    if (child.type !== "Attdef") continue;
    if (child.flags.constant) {
      constantTags.add(child.tag);
      continue;
    }
    const list = attdefsByTag.get(child.tag);
    if (list) list.push(child);
    else attdefsByTag.set(child.tag, [child]);
  }

  const liveLocalPoints: Wcs3[] = [];
  const overrideBypassPoints: Wcs3[] = [];
  const seenByTag = new Map<string, number>();
  for (const attr of attributes) {
    const occurrenceIndex = seenByTag.get(attr.tag) ?? 0;
    seenByTag.set(attr.tag, occurrenceIndex + 1);
    const attdef = attdefsByTag.get(attr.tag)?.[occurrenceIndex];

    // @codex T1 arm(c) round-19 P2 finding (F8): an ATTRIB hidden by an EATTEDIT per-instance
    // override, or by the matched ATTDEF's own `flags.invisible` when no override is present,
    // contributes NOTHING — mirrors `attdefFromEntity.ts::resolveInvisible` exactly. Checked before
    // the occurrence-index dispatch below so an invisible row never fabricates a box the renderer
    // never draws (matched, world-placed, OR orphan).
    if (attr.invisible ?? attdef?.flags.invisible ?? false) continue;

    // F7 — a same-tag row beyond the real (non-constant) occurrence count, whose tag belongs to a
    // CONSTANT ATTDEF, is a stray the constant authoritatively swallows regardless of
    // `overridePosition` (mirrors the renderer's `blockFromEntity.ts` orphan-pass check running
    // BEFORE `orphanAttributeFromEntity` is even called — see doc).
    if (attdef === undefined && constantTags.has(attr.tag)) continue;

    if (attr.overridePosition) {
      // A WORLD placement's unset height/rotation default to the literal DXF-group defaults the
      // renderer (`attdefFromEntity.ts::resolvePlacement`/`orphanAttributeFromEntity`) and export
      // encoder (`dxfBridge.attribExportPosition.ts`, round-4/6) both use — NOT the ATTDEF's own
      // `height`/`rotation` (those apply only to the block-local LIVE-template regime below).
      // Justification/widthFactor/generationFlags/obliqueAngle source from the matching ATTDEF
      // when found, else the orphan fallback's PLAIN style (round-10 finding). F6 — multiline
      // (`multilineAttributeLocalCorners`, see its doc) instead of a single `textJustifiedLocalBox`.
      const height = attr.overrideHeight ?? 2.5;
      const rotation = attr.overrideRotation ?? 0;
      const corners = multilineAttributeLocalCorners(
        attr.overridePosition,
        undefined,
        height,
        rotation,
        attr.value,
        attdef?.widthFactor ?? 1,
        attdef?.hJustification ?? 0,
        attdef?.vJustification ?? 0,
        // GitHub Codex review PR #558 round 2 P2 — the EFFECTIVE WORLD
        // generation-flags override (mirrors `attdefFromEntity.ts
        // ::resolvePlacement`'s `attr?.overrideGenerationFlags ?? 0` for a
        // WORLD-placed attribute EXACTLY), not the matched ATTDEF's own
        // (render-irrelevant, for this regime) `generationFlags`.
        attr.overrideGenerationFlags ?? 0,
        attdef?.obliqueAngle ?? 0,
      );
      for (const p of corners) overrideBypassPoints.push({ x: p.x, y: p.y, z: 0 });
      // @codex T1 arm(c) round-25 P2 finding (Fix 3) — a matched context's own `insertion` is a
      // BLOCK-LOCAL point that RIDES the enclosing INSERT transform (confirmed:
      // `attdefFromEntity.ts::applyAnnotativePlacement`'s round-18 doc — "That insertion is a
      // BLOCK-LOCAL point ... so it has to ride the FULL enclosing INSERT affine", and it
      // UNCONDITIONALLY overrides the placement, including a WORLD `overridePosition` regime,
      // whenever a context matches), unlike the WORLD `overridePosition` point above (which
      // bypasses the transform). So a context box for THIS attribute goes into `liveLocalPoints`
      // (transform-riding), never `overrideBypassPoints`, even though the base `attr.value`'s own
      // placement here is a bypass one.
      if (attdef) {
        // NOTE — tracked as https://github.com/cosc/Edit-dwg/issues/563
        // ("WCS bounds under-reports height for a WORLD-placed annotative
        // ATTRIB's matched-context box") — see that issue for the full
        // root-cause writeup and completion plan. Short version: `height`
        // here is the RAW (uncompensated) WORLD-attribute height, and this
        // box rides the FULL enclosing INSERT chain (both this level's own
        // scale AND any outer nesting) via `applyFullInsertChain` below —
        // whereas the renderer (`attdefFromEntity.ts`'s
        // `worldHeightBypassesBlockScale`/`immediateHeightScale`) makes a
        // WORLD attribute's height bypass ONLY the enclosing INSERT's OWN
        // immediate scale, inheriting further-outer nesting only. For a
        // non-unit immediate scale this box's reported height diverges from
        // the actually-rendered one (under-reports for |scaleY| < 1 — the
        // exact failure mode this file's own "never under-report" design
        // otherwise avoids). Not a one-line fix: `liveLocalPoints` here are
        // built ONCE, chain-agnostic, then reused across MULTIPLE different
        // `chainEntity` transforms at both call sites (master + every
        // `blockrefAnnotativeTransforms` context) — the compensation needs
        // to happen PER chainEntity's own scale, not at this
        // chain-agnostic construction point. Not introduced by PR #558
        // (this file's height computation predates #558 touching it at all,
        // confirmed via `git diff origin/main...HEAD`); #558's own
        // render-side double-scaling fix (rounds 9-10,
        // `attdefFromEntity.ts`) exposed rather than created this
        // pre-existing, separate gap.
        for (const p of attributeContextLocalCorners(
          attdef,
          attr.value,
          height,
          attdef.widthFactor,
          attdef.hJustification,
          attdef.vJustification,
          // GitHub Codex review PR #558 round 2 (round-3 local read-only
          // follow-up) P2 — this context box belongs to the SAME WORLD
          // attribute the master box just above resolved with the
          // EFFECTIVE override (`attr.overrideGenerationFlags ?? 0`), not
          // `attdef.generationFlags`. `attdefFromEntity.ts`'s round-3 fix
          // (`renderAttributeValueLines`'s `generationFlags` gate on
          // `attr?.overridePosition !== undefined`) established that
          // positioning and handedness are INDEPENDENT per-instance facts —
          // a matched annotative context changes WHERE this attribute's
          // context-scale box sits (a block-local point riding the INSERT
          // affine, per the comment above), never WHICH flags it carries.
          // Using the template's flags here let a mirrored asymmetric
          // WORLD attribute's context-scale box extend on the WRONG side,
          // missing it from window selection at that annotation scale.
          attr.overrideGenerationFlags ?? 0,
          attdef.obliqueAngle,
        )) {
          liveLocalPoints.push(throughOwnOcs({ x: p.x, y: p.y, z: 0 }, attdef.extrusion));
        }
      }
      continue;
    }
    if (attdef) {
      // F6 — multiline (`multilineAttributeLocalCorners`, see its doc).
      const height = attr.overrideHeight ?? attdef.height;
      const rotation = attr.overrideRotation ?? attdef.rotation;
      const corners = multilineAttributeLocalCorners(
        attdef.insertionPoint,
        attdef.alignmentPoint,
        height,
        rotation,
        attr.value,
        attdef.widthFactor,
        attdef.hJustification,
        attdef.vJustification,
        attdef.generationFlags,
        attdef.obliqueAngle,
      );
      // The ATTDEF's own extrusion applies (ATTDEF is an OCS entity like TEXT) BEFORE the
      // enclosing insert chain — mirrors every other block-definition-child kind's transform order.
      for (const p of corners) liveLocalPoints.push(throughOwnOcs({ x: p.x, y: p.y, z: 0 }, attdef.extrusion));
      // @codex T1 arm(c) round-25 P2 finding (Fix 3) — union EVERY `kind: "attribute"` annotative
      // context's own box too (see {@link attributeContextLocalCorners}'s doc) — the SAME "union
      // master with every context" pattern `textLocal`/`mtextLocal`/`multileaderWcsBounds` already
      // establish, applied here for the matched-ATTDEF (Phase A) regime.
      for (const p of attributeContextLocalCorners(
        attdef,
        attr.value,
        height,
        attdef.widthFactor,
        attdef.hJustification,
        attdef.vJustification,
        attdef.generationFlags,
        attdef.obliqueAngle,
      )) {
        liveLocalPoints.push(throughOwnOcs({ x: p.x, y: p.y, z: 0 }, attdef.extrusion));
      }
      continue;
    }
    // ORPHAN ATTRIB, `overridePosition` UNSET — the renderer's "block-origin fallback": PLAIN
    // style at LOCAL (0,0), riding the full INSERT chain like a live-template attribute (no OCS
    // extrusion of its own — there is no ATTDEF to source one from). F6 — multiline.
    const height = attr.overrideHeight ?? 2.5;
    const rotation = attr.overrideRotation ?? 0;
    // GitHub Codex review PR #558 round 2 P2 — `attdefFromEntity.ts
    // ::orphanAttributeFromEntity` reads `attr.overrideGenerationFlags ?? 0`
    // for EVERY orphan regime (WORLD-placed AND the block-origin fallback
    // here — its own `generationFlags: attr.overrideGenerationFlags ?? 0`
    // is unconditional on `worldPlacement`), not a hardcoded 0.
    const corners = multilineAttributeLocalCorners({ x: 0, y: 0 }, undefined, height, rotation, attr.value, 1, 0, 0, attr.overrideGenerationFlags ?? 0, 0);
    for (const p of corners) liveLocalPoints.push({ x: p.x, y: p.y, z: 0 });
  }

  // F3 (@codex T1 arm(c) round-21 P2, see this function's own doc) — every NON-CONSTANT ATTDEF
  // occurrence the main loop above never visited (this tag's real `attributes[]` row count,
  // `seenByTag`, is at or below that occurrence's index) falls back to the SAME
  // `defaultValue || tag || "?"` template box the render path draws for its OWN `attr === undefined`
  // case (`attdefFromEntity.ts::resolveAttributeValue`) — mirrors the matched-ATTDEF branch above
  // exactly (same `multilineAttributeLocalCorners` + own-OCS `throughOwnOcs` transform), just
  // sourcing every field from the ATTDEF template instead of a real `attr` row. Runs for EVERY call
  // site unconditionally — see this function's own doc for why a top-level-only gate regressed a
  // nested block's uncovered-tag case to reporting nothing.
  {
    for (const [tag, occurrences] of attdefsByTag) {
      const consumed = seenByTag.get(tag) ?? 0;
      for (let i = consumed; i < occurrences.length; i++) {
        const attdef = occurrences[i]!;
        // F1 (@codex T1 arm(c) round-21 P2): an occurrence with NO per-instance row has no EATTEDIT
        // override to consult either — only the ATTDEF's own `flags.invisible` can gate it (mirrors
        // `resolveInvisible`'s constant branch / the matched-row check above, `attr?.invisible`
        // simply does not exist here).
        if (attdef.flags.invisible) continue;
        const fallbackValue = attdef.defaultValue || attdef.tag || "?";
        const corners = multilineAttributeLocalCorners(
          attdef.insertionPoint,
          attdef.alignmentPoint,
          attdef.height,
          attdef.rotation,
          fallbackValue,
          attdef.widthFactor,
          attdef.hJustification,
          attdef.vJustification,
          attdef.generationFlags,
          attdef.obliqueAngle,
        );
        for (const p of corners) liveLocalPoints.push(throughOwnOcs({ x: p.x, y: p.y, z: 0 }, attdef.extrusion));
        // @codex T1 arm(c) round-25 P2 finding (Fix 3) — union EVERY `kind: "attribute"` annotative
        // context's own box too, same pattern as the matched-ATTDEF branch above, applied to this
        // "no matching row" template-fallback regime.
        for (const p of attributeContextLocalCorners(
          attdef,
          fallbackValue,
          attdef.height,
          attdef.widthFactor,
          attdef.hJustification,
          attdef.vJustification,
          attdef.generationFlags,
          attdef.obliqueAngle,
        )) {
          liveLocalPoints.push(throughOwnOcs({ x: p.x, y: p.y, z: 0 }, attdef.extrusion));
        }
      }
    }
  }

  return { liveLocalPoints, overrideBypassPoints };
}

/**
 * Tile a set of already-anchored `overrideBypassPoints` across a MINSERT grid's bounds-
 * SUFFICIENT extremal corner offsets (`minsertGridOffsets2D`), riding the array's own
 * scale/rotate/OCS-extrusion — the SAME linear map every other grid cell's geometry rides — but
 * NOT its basePoint-subtract/insertionPoint-translate (these points are already anchored, not
 * block-definition-local; only the CELL STEP needs to reach them).
 *
 * @codex T1 arm(c) round-10 P2 finding: the renderer repeats an `overridePosition`-set ATTRIB at
 * EVERY MINSERT cell, shifted by that cell's grid offset "in the block's own rotated/scaled
 * frame" (`attdefFromEntity.ts` module header, "finding 2 (MINSERT)":
 * `offset = transformBlockDirection(cellOffsetRaw, transform)`, a PURE scale+rotate linear map,
 * no basePoint/insertionPoint) — the prior code unioned `overrideBypassPoints` only ONCE (the
 * base cell), missing every other cell for `columnCount`/`rowCount` > 1.
 *
 * Reusing ONLY the 4 extremal grid corners (not every cell) is exact, not an approximation — the
 * per-cell delta is LINEAR in the grid offset (scale·rotate·OCS, no translation), so
 * `{point + delta(offset) : offset ∈ grid}`'s AABB is the Minkowski-sum-AABB `point +
 * AABB(delta(grid))`, and `delta` being linear means only the grid's own (rectangle) convex-hull
 * corners can extend that AABB (`minsertGridOffsets2D`'s own doc has the full derivation).
 */
function tileAcrossMinsertCells(points: readonly Wcs3[], e: BlockInsertEntity, axes: OcsAxes): Wcs3[] {
  if (points.length === 0) return [];
  const offsets = minsertGridOffsets2D(e.columnCount, e.rowCount, e.columnSpacing, e.rowSpacing);
  const out: Wcs3[] = [];
  for (const offset of offsets) {
    if (offset.x === 0 && offset.y === 0) {
      out.push(...points);
      continue;
    }
    // @codex T1 arm(c) round-19 P2 finding (F2): the SAME `guardInsertScale` the top-level insert
    // chain applies (`transformOnePoint`'s doc) — a zero/non-finite scale component must not
    // collapse the MINSERT cell-delta step to zero either.
    const r = rotateDeg2D(offset.x * guardInsertScale(e.scaleX), offset.y * guardInsertScale(e.scaleY), e.rotation ?? 0);
    const delta = ocsToWcs({ x: r.x, y: r.y, z: 0 }, axes);
    for (const p of points) out.push({ x: p.x + delta.x, y: p.y + delta.y, z: p.z + delta.z });
  }
  return out;
}

/**
 * A CONSTANT ATTDEF's own instance-agnostic LOCAL text-box corners (block-definition frame,
 * BEFORE the child's own OCS `extrusion` — the caller applies that, see `childLocalPointSet`'s
 * `Attdef` arm) — the ONE truth for "what a constant ATTDEF draws" that BOTH this module's cached
 * per-block-NAME point set AND `@dxf-ai-editor/cad-core`'s memoized definition footprint
 * (`blockGeometry.ts::childEntityBounds`, Codex PR #784 round 2 P2) union into a block's bounds.
 * Exported for exactly that reuse (cad-core's `attdefBounds` is anchor-only, while the renderer
 * — `scene3d/adapters/typed/attdefFromEntity.ts` — draws the constant's `defaultValue`
 * unconditionally, so an anchor-only box clipped the label text).
 *
 * Returns `[]` for an INVISIBLE ATTDEF (renders nothing) and for a VARIABLE (non-constant) one —
 * a variable ATTDEF's box is per-INSTANCE (the row's value, or the no-row template fallback) and
 * lives in {@link resolveAttributeContribution}, never in an instance-agnostic cache.
 */
export function constantAttdefLocalCorners(child: AttdefEntity): Local2[] {
  // F1 (@codex T1 arm(c) round-21 P2 finding): an invisible ATTDEF (`flags.invisible`) renders
  // NOTHING (`attdefFromEntity.ts::resolveInvisible`'s constant branch: `attdef.flags.constant ?
  // attdef.flags.invisible : ...` — the invisible check is gated on the ATTDEF's OWN flag
  // regardless of constant-ness). This CACHED, per-block-definition-NAME memoized path has no
  // per-instance ATTRIB/EATTEDIT override to consult (see F3 below for why) — only the ATTDEF's
  // own flag can be checked here; a per-instance `invisible` override is handled entirely within
  // `resolveAttributeContribution`'s per-instance path (round-19 F8), independent of this cache.
  if (child.flags.invisible) return [];

  // F3 (@codex T1 arm(c) round-21 P2 finding — architectural): a NON-CONSTANT (variable) ATTDEF
  // contributes NOTHING to this CACHED, instance-agnostic point set — `resolveBlockLocalPoints`
  // memoizes per block-definition NAME (see that function's own doc), so it is shared across
  // EVERY INSERT of this block and cannot know whether any given instance supplies a real ATTRIB
  // for this occurrence. The render path substitutes `attr.value` whenever a matching ATTRIB row
  // exists and never draws this template fallback at all in that case — round-20's fix (caching
  // `defaultValue || tag || "?"` here unconditionally) was correct ONLY for an instance with NO
  // matching row, but applied it to EVERY instance, double-counting the template box alongside a
  // real ATTRIB's own (narrower) value box. The "no matching row" fallback now lives in
  // `resolveAttributeContribution` (the per-instance path, which DOES know per-instance/per-
  // occurrence row presence via its `seenByTag` F7 occurrence tracking — see its own doc) —
  // architecturally moved OUT of this cache, not dropped. A CONSTANT ATTDEF (`child.flags
  // .constant`) is UNAFFECTED: its `defaultValue` is authoritative regardless of any instance's
  // `attributes[]` (matches `resolveAttributeValue`'s constant branch, round-19 F6/round-20's own
  // doc), so it alone still proceeds to build a cached, unconditional box below.
  if (!child.flags.constant) return [];

  // @codex T1 arm(c) round-23 P2 finding: MULTILINE, not single-line — a constant ATTDEF whose
  // `defaultValue` contains embedded newlines was still built as ONE `textJustifiedLocalBox`
  // spanning the whole string. The renderer's constant-ATTDEF path
  // (`attdefFromEntity.ts::attdefFromEntity`) sends the resolved value through
  // `renderAttributeValueLines`, which splits on embedded newlines and stacks each line as its
  // own TEXT row at the SAME `ATTRIBUTE_LINE_PITCH_FACTOR` line pitch this module's
  // {@link multilineAttributeLocalCorners} already replicates for the VARIABLE-ATTDEF fallback
  // and both `overridePosition`/matched-ATTDEF branches of {@link resolveAttributeContribution}
  // (round-19 F6/round-21 F3) — this was the ONE remaining call site still using the single-line
  // `textJustifiedLocalBox` directly. `multilineAttributeLocalCorners` reduces to byte-identical
  // single-line behavior for a `defaultValue` with no embedded newline (its own doc), so this is
  // a strict widening for the multiline case only.
  const corners = multilineAttributeLocalCorners(
    child.insertionPoint,
    child.alignmentPoint,
    child.height,
    child.rotation,
    // A CONSTANT ATTDEF's `defaultValue` is the final value, used VERBATIM even when empty —
    // no tag/"?" fallback (matches `resolveAttributeValue`'s constant branch exactly; the
    // variable-ATTDEF tag/"?" fallback chain no longer lives in this cached path at all — F3).
    child.defaultValue,
    child.widthFactor,
    child.hJustification,
    child.vJustification,
    child.generationFlags,
    child.obliqueAngle,
  );
  // @codex T1 arm(c) round-25 P2 finding (Fix 3) — union EVERY `kind: "attribute"` annotative
  // context's own box too (see {@link attributeContextLocalCorners}'s doc) — this CACHED,
  // instance-agnostic path is exactly where a CONSTANT ATTDEF's own context list belongs (its
  // `defaultValue` is instance-invariant, and so is its `annotativeContexts[]` — both live on
  // the ATTDEF template, not any per-instance ATTRIB row). Raw LOCAL corners, like the master
  // above — the caller's single `applyOwnOcs` below applies `child.extrusion` to the WHOLE
  // combined point set uniformly (adding an already-OCS'd point here would double-transform it).
  const contextCorners = attributeContextLocalCorners(
    child,
    child.defaultValue,
    child.height,
    child.widthFactor,
    child.hJustification,
    child.vJustification,
    child.generationFlags,
    child.obliqueAngle,
  );
  return [...corners, ...contextCorners];
}

/**
 * One BLOCK-DEFINITION-CHILD entity's local point set — dispatches to the shared leaf builders +
 * ATTDEF/Block. Every CURVED kind (Circle/Arc/Ellipse/LWPOLYLINE-bulge/Revision-Cloud-bulge/
 * Hatch arc+ellipticArc edges) is dispatched with `dense=true` (@codex T1 arm(c) P1 finding): a
 * block child undergoes a FURTHER, a-priori-unknown rotation from the enclosing INSERT chain
 * (this level's own `applyFullInsertChain` call, PLUS every ancestor level for a nested block),
 * so the child's own extrusion state alone cannot gate tessellation the way it safely does for a
 * TOP-LEVEL entity (`wcsBounds.ts`, which has no further external rotation coming) — see
 * `wcsBoundsLeaf.ts::circleLocal`'s doc for the full reasoning.
 */
function childLocalPointSet(
  child: BlockChildEntity,
  blocks: Readonly<Record<string, BlockDefinition>>,
  resolved: Map<string, readonly Wcs3[] | typeof ALWAYS_INCLUDE>,
  inProgress: Set<string>,
  dimensionStyles: Readonly<Record<string, DimensionStyle>> | undefined,
): Wcs3[] | typeof ALWAYS_INCLUDE {
  switch (child.type) {
    case "Line":
      return applyOwnOcs(lineLocal(child));
    case "Point":
      return applyOwnOcs(pointLocal(child));
    case "Circle":
      return applyOwnOcs(circleLocal(child, true));
    case "Arc":
      return applyOwnOcs(arcLocal(child, true));
    case "Ellipse":
      return applyOwnOcs(ellipseLocal(child, true));
    case "Spline":
      return applyOwnOcs(splineLocal(child));
    case "LWPOLYLINE":
      return applyOwnOcs(lwpolylineLocal(child, true));
    case "Text":
      return applyOwnOcs(textLocal(child));
    case "MText":
      return applyOwnOcs(mtextLocal(child));
    case "Hatch":
      return applyOwnOcs(hatchLocal(child, true));
    case "Dimension":
      // @codex T1 arm(c) round-22 P2 finding: resolve THIS child's named DIMSTYLE (`child.styleName`)
      // against the threaded `dimensionStyles` table (falling back to "Standard", see
      // `resolveDimensionStyleFromContext`'s doc), same as the top-level `wcsBounds.ts` dispatch —
      // a block-definition-child Dimension using a named style is no less real than a top-level one.
      return applyOwnOcs(dimensionLocal(child, resolveDimensionStyleFromContext(child.styleName, dimensionStyles)));
    case "Multileader":
      // Leader/anchor/MText-content-BOX geometry (@codex T1 arm(c) round-19 P2 finding — F5:
      // `multileaderLeafLocal` now unions the SAME `centeredTextBoxLocal` text box the top-level
      // `wcsBounds.ts`'s Multileader case unions for `contentType===2`, see that helper's doc).
      // BLOCK-content (contentType===1) resolution is still a top-level-only concern (a
      // block-child MLEADER's own referenced-block footprint is a documented residual gap,
      // mirroring cad-core's leafBounds/blockGeometry split — kept minimal here to avoid a second
      // cycle-guard thread).
      return applyOwnOcs(multileaderLeafLocal(child));
    case "Revision Cloud":
      return applyOwnOcs(revisionCloudLocal(child, true));
    case "Attdef": {
      // ONE truth for the constant-ATTDEF box — {@link constantAttdefLocalCorners} (`[]` for an
      // invisible or variable ATTDEF; see its doc for the F1 / F3 / round-23 / round-25 rationale).
      const pts: Wcs3[] = constantAttdefLocalCorners(child).map((p) => ({ x: p.x, y: p.y, z: 0 }));
      return applyOwnOcs({ points: pts, extrusion: child.extrusion });
    }
    case "Block": {
      const defPts = resolveBlockLocalPoints(child.blockName, blocks, resolved, inProgress, dimensionStyles);
      if (defPts === ALWAYS_INCLUDE) return ALWAYS_INCLUDE;
      // @codex T1 arm(c) round-24 P2 finding: own-property-safe lookup (see `safeRecordLookup`'s
      // doc) — a bare `blocks[child.blockName]` index falls through to an inherited
      // `Object.prototype` member for a block name like `"constructor"`.
      const def = safeRecordLookup(blocks, child.blockName);
      const defBasePoint = def?.basePoint;
      // @codex T1 arm(c) round-6 P2 finding-2: a NESTED insert's own `attributes[]` (baked into
      // the ENCLOSING definition's authored content, hence fixed regardless of how many times the
      // OUTER block is itself top-level-instantiated — safe through this memoized per-block-NAME
      // path, see `resolveAttributeContribution`'s doc) are no longer dropped.
      const { liveLocalPoints, overrideBypassPoints } = resolveAttributeContribution(child.attributes, def);
      // @codex T1 arm(c) round-25 P2 finding (Fix 4) — a NESTED `Block` child's OWN "blockref"
      // annotative contexts (mirrors {@link blockrefAnnotativeTransforms}, applied at TOP-LEVEL by
      // `blockInsertWcsBounds` since round-24) were not applied at all here: round-24's fix covered
      // only the top-level INSERT dispatch, leaving a nested child's own `annotativeContexts` (a
      // NESTED INSERT is itself schema-eligible for annotative representations, same
      // `blockInsertEntitySchema.annotativeContexts` field a top-level INSERT carries) silently
      // ignored. Union the master transform's footprint with EVERY "blockref" context's own —
      // same union pattern, applied at this nesting level too (composes correctly into whatever
      // ANCESTOR chain calls this: each level's own union is independent of the levels above it).
      const chainEntities = [child, ...blockrefAnnotativeTransforms(child)];
      const out: Wcs3[] = [];
      for (const chainEntity of chainEntities) {
        out.push(...applyFullInsertChain([...defPts, ...liveLocalPoints], chainEntity, defBasePoint));
        if (overrideBypassPoints.length > 0) {
          // @codex T1 arm(c) round-10 P2 finding: tile across THIS nested insert's own MINSERT grid
          // (see `tileAcrossMinsertCells`'s doc) — a nested Block child can itself be a MINSERT.
          // @codex T1 arm(c) round-25 P2 finding (Fix 5, mirrored here for nested-child parity):
          // tile against EACH chainEntity's own scale/rotation, not just the master's — see
          // `blockInsertWcsBounds`'s own round-25 Fix 5 doc for the full rationale.
          out.push(...tileAcrossMinsertCells(overrideBypassPoints, chainEntity, computeOcsAxes(chainEntity.extrusion)));
        }
      }
      return out;
    }
    case "Polyline":
      // `dense=true` for the same reason every other curved child is dense
      // here: a block child takes a further, a-priori-unknown rotation from
      // the enclosing INSERT chain, so a bulged segment's local cardinal
      // extrema are not guaranteed to stay extremal (see this function's doc).
      return applyOwnOcs(heavyPolylineLocal(child, true));
    case "Table": {
      // A block-child ACAD_TABLE resolves the `*T<N>` graphics block it
      // references, in the SAME local frame the child lives in — through
      // {@link tableLocalPointSet}, the SAME resolution `tableWcsBounds`
      // itself runs. `ALWAYS_INCLUDE` from an unresolvable/cyclic block
      // propagates verbatim, matching how every other block-backed child
      // reports an unknown footprint here. A definition that RESOLVES but nets
      // to zero points now contributes `[]` (nothing), exactly like the nested
      // `Block` arm above — the prior `!tableBox` test conflated that documented
      // "resolved, no geometry" case (see `resolveBlockLocalPoints`' own doc)
      // with "unresolvable" purely because an AABB cannot express the difference.
      //
      // @codex round-5 P2 finding A ("Transform every nested table-box
      // corner"): the prior code called `tableWcsBounds` and reduced its
      // AABB to the min/min and max/max DIAGONAL before handing the two
      // points to the ENCLOSING insert chain. An axis-aligned box is closed
      // only under axis-aligned maps: as soon as the enclosing INSERT
      // carries an oblique rotation, the image of those two corners does NOT
      // bound the image of the rectangle — the two OMITTED cross-corners
      // (min/max and max/min) are exactly the ones that can attain the
      // rotated extrema. For a 20×10 table under a 45° enclosing INSERT the
      // diagonal spans X∈[0, 7.07] while the true footprint spans
      // X∈[-7.07, 14.14], so `computeEntityWcsBounds` under-reported the
      // INSERT and agent-facing `entityBounds` queries could exclude visible
      // table graphics.
      //
      // @codex round-5 P2 finding B ("Preserve the active cycle guard across
      // table references"): the prior `tableWcsBounds` call also started a
      // FRESH `inProgress` set, so a block-reference cycle that crosses a
      // table (block `A` → its table's `*T<N>` graphics block → INSERT of
      // `A`) forgot every ancestor under resolution and recursed to a stack
      // overflow. Threading THIS call's own `resolved`/`inProgress` — exactly
      // what the nested `Block` arm above already does — makes the shared
      // `resolveDefinitionWithCycleGuard` see the ancestor and return the
      // documented `ALWAYS_INCLUDE` unknown bound instead.
      return tableLocalPointSet(child, blocks, resolved, inProgress, dimensionStyles);
    }
    case "OLE Frame":
      return applyOwnOcs(oleFrameLocal(child));
    default: {
      const exhaustive: never = child;
      void exhaustive;
      return [];
    }
  }
}

function applyOwnOcs(set: LocalPointSet): Wcs3[] {
  return set.points.map((p) => throughOwnOcs(p, set.extrusion));
}

/**
 * MULTILEADER leaf-only local points (leader/anchor/MText-content-BOX — no BLOCK-content
 * resolution; see above). `Pick<MultileaderEntity, ...>` (not an ad-hoc structural shape):
 * `leaderRoots`' nested `node` must structurally BE the real `MleaderLeaderNode` (every field,
 * including the schema's OTHER-than-`.optional()` `.default(...)` fields like
 * `breakPoints`/`arrowheadBlock` on each leader line) to satisfy
 * `multileaderDoglegEnd`/`multileaderNodeLastVertex`'s own `Pick<MleaderLeaderNode, ...>`
 * parameter types — a narrower hand-written shape (even one that looks structurally compatible at
 * a glance) fails those functions' stricter Pick.
 *
 * @codex T1 arm(c) round-19 P2 finding (F5): the `contentType`/`textContents`/`textHeight`/
 * `textRotation`/`textWidth`/`textLineSpacingFactor` fields are now part of the Pick too, so an
 * MTEXT-content (`contentType===2`) block-child MLEADER can union the SAME `centeredTextBoxLocal`
 * text box the top-level `wcsBounds.ts::multileaderWcsBounds` case unions — previously this
 * block-child path accepted only leader/anchor fields and reported a leaderless/short-leader
 * instance's bounds collapsed to the anchor, missing the rendered multi-line/wide text box.
 *
 * @codex T1 arm(c) round-23 P2 finding: `"id"` is now part of the Pick too — a text-content MLEADER
 * with an EMPTY `textContents` still draws real text (see the `contentType === 2` branch below).
 *
 * @codex T1 arm(c) round-24 P2 finding (F6): `"arrowheadSize"`/`"arrowheadBlock"` are now part of
 * the Pick too — a visible leader's arrowhead pad (see the round-21 F7-mirroring block below) needs
 * them, same as the top-level `wcsBounds.ts::multileaderWcsBounds` case already has.
 *
 * @codex T1 arm(c) round-24 P2 finding: `"annotativeContexts"` is now part of the Pick too — a
 * block-child MLEADER's own annotative-context text-box union (see the `contentType === 2` branch
 * below) needs it, mirroring the top-level `wcsBounds.ts::multileaderWcsBounds` fix for the same
 * finding.
 *
 * @codex T1 arm(c) round-26 P2 finding (Fix 3): `"landingGap"` is now part of the Pick too — the
 * dogleg→landing-gap endpoint (see the {@link multileaderLandingEnd} call below) needs it, mirroring
 * the top-level `wcsBounds.ts::multileaderWcsBounds`/`multileaderContextLeaderPad`'s own round-25
 * Fix 1 for the same finding (this block-child path previously added only the dogleg endpoint,
 * never the further landing-gap segment past it).
 */
function multileaderLeafLocal(
  e: Pick<
    MultileaderEntity,
    | "id"
    | "textLocation"
    | "basePoint"
    | "leaderLineType"
    | "landingEnabled"
    | "doglegEnabled"
    | "doglegLength"
    | "landingGap"
    | "leaderRoots"
    | "contentType"
    | "textContents"
    | "textHeight"
    | "textRotation"
    | "textWidth"
    | "textLineSpacingFactor"
    | "arrowheadSize"
    | "arrowheadBlock"
    | "annotativeContexts"
  >,
): LocalPointSet {
  const pts: Wcs3[] = [{ x: e.textLocation.x, y: e.textLocation.y, z: 0 }];
  if (e.basePoint) pts.push({ x: e.basePoint.x, y: e.basePoint.y, z: 0 });
  // @codex T1 arm(c) round-12 P2 finding: the SAME spline-leader control-hull + dogleg-endpoint
  // fix as the top-level `wcsBounds.ts::multileaderWcsBounds` case (see its doc) — a block-child
  // MLEADER's leader geometry undergoes this level's own OCS/INSERT transform just like every
  // other block-child kind, so it must be included here too, not just at the top level.
  const isSpline = e.leaderLineType === 2;
  // @codex T1 arm(c) round-24 P2 finding (F6): the SAME arrowhead pad the top-level
  // `wcsBounds.ts::multileaderWcsBounds` case applies (round-21 F7 — see that doc for the full
  // per-variant footprint derivation) — a block-child MLEADER's arrowhead extends past the first
  // leader-line vertex exactly like a top-level one; the prior code here added only the leader
  // vertices/control points/dogleg, reporting a block/entity bound that stopped at the arrow tip.
  const arrowVisible = e.leaderLineType !== 0;
  for (const root of e.leaderRoots) {
    for (const node of root.nodes) {
      if (node.lastLeaderPoint) pts.push({ x: node.lastLeaderPoint.x, y: node.lastLeaderPoint.y, z: node.lastLeaderPoint.z });
      for (const line of node.lines) {
        for (const v of line.vertices) pts.push({ x: v.x, y: v.y, z: v.z ?? 0 });
        if (isSpline) {
          for (const c of splineLeaderControlPoints(line.vertices)) pts.push({ x: c.x, y: c.y, z: c.z });
        }
        const resolvedArrow = line.arrowheadBlock || e.arrowheadBlock;
        if (arrowVisible && line.vertices.length > 0 && resolvedArrow !== "_NONE") {
          const tip = line.vertices[0]!;
          const pad = e.arrowheadSize;
          pts.push({ x: tip.x + pad, y: tip.y + pad, z: tip.z ?? 0 });
          pts.push({ x: tip.x - pad, y: tip.y - pad, z: tip.z ?? 0 });
        }
      }
      const doglegEnd = multileaderDoglegEnd(e, node, multileaderNodeLastVertex(e, node));
      if (doglegEnd) {
        pts.push({ x: doglegEnd.x, y: doglegEnd.y, z: 0 });
        // @codex T1 arm(c) round-26 P2 finding (Fix 3): the landing-gap endpoint past the dogleg —
        // see {@link multileaderLandingEnd}'s own doc; mirrors the top-level round-25 Fix 1 union.
        const landingEnd = multileaderLandingEnd(e.textLocation, doglegEnd, e.landingGap);
        pts.push({ x: landingEnd.x, y: landingEnd.y, z: 0 });
      }
    }
  }
  // @codex T1 arm(c) round-19 P2 finding (F5): union the text-content box for an MTEXT-content
  // (contentType===2) block-child MLEADER — byte-identical to the top-level
  // `wcsBounds.ts::multileaderWcsBounds` union (same `centeredTextBoxLocal` call, same
  // `textWidth`/`textLineSpacingFactor` honoring).
  //
  // @codex T1 arm(c) round-23 P2 finding: an EMPTY `textContents` on a text-content MLEADER is NOT
  // "no text" — mirrors round-21's F8 fix for the TOP-LEVEL `wcsBounds.ts::multileaderWcsBounds`
  // case EXACTLY (same fallback string, `multileaderFromEntity.ts:122`'s `entity.textContents ||
  // \`MLeader ${entity.id}\``): the typed renderer substitutes that fallback string BEFORE building
  // the text primitive REGARDLESS of whether the MLEADER is a top-level entity or a block-definition
  // child, so a block-child MLEADER with empty `textContents` still draws (and must still be
  // bounded for) that same fallback text box. The prior `e.textContents.length > 0` guard here
  // dropped the whole text box whenever `textContents` was empty — under-reporting the resolved
  // block's bounds for this common "un-annotated placeholder MLEADER" case.
  //
  // @codex T1 arm(c) round-24 P2 finding: union EVERY `annotativeContexts[]` representation's own
  // text box too, mirroring the top-level `wcsBounds.ts::multileaderWcsBounds` fix for the SAME
  // finding (see that doc for the full renderer-resolution derivation) — a block-child MLEADER
  // resolves its annotative context through the identical `pickAnnotativeContext` path the renderer
  // uses regardless of nesting, so the same "never under-report" union applies here too.
  if (e.contentType === 2) {
    const contents = e.textContents || `MLeader ${e.id}`;
    // GitHub Codex review PR #558 round 2 P2 — `e.textRotation` /
    // `context.textRotation` are RADIANS (see `radToDeg`'s own doc, same
    // fix as the top-level `multileaderWcsBounds` above);
    // `centeredTextBoxLocal`'s `rotationDeg` param needs DEGREES.
    pts.push(...centeredTextBoxLocal(e.textLocation, contents, e.textHeight, radToDeg(e.textRotation), e.textWidth, e.textLineSpacingFactor));
    for (const context of e.annotativeContexts ?? []) {
      pts.push(...centeredTextBoxLocal(e.textLocation, contents, context.textHeight, radToDeg(context.textRotation), context.textWidth, e.textLineSpacingFactor));
    }
  }
  return { points: pts, extrusion: undefined };
}

/**
 * Resolve a block DEFINITION's model-space child-geometry local point set, recursively
 * transforming nested INSERT children (incl. their own MINSERT grids, base points, and OCS).
 * Memoized + cycle-safe via the shared `resolveDefinitionWithCycleGuard` (`viewportMembership.ts`
 * — one-truth memoize+cycle-guard skeleton, reused rather than re-derived). `ALWAYS_INCLUDE`
 * marks an unresolvable definition (missing / cyclic / no usable geometry).
 *
 * `def.rawEntityGroups` (unsupported/raw passthrough children) contribute NOTHING — they carry
 * no typed geometry to bound (documented gap, mirrors `cad-core/blockGeometry.ts`'s identical
 * scope: only `def.entities` is walked). @codex T1 arm(c) round-23 P2 finding: a definition that
 * ALSO carries a non-empty `rawEntityGroups` (a MIXED block — some typed children this function
 * resolves, some unsupported/raw children preserved verbatim for export round-trip, e.g. a typed
 * LINE alongside an unsupported ACAD_TABLE) is collapsed to `ALWAYS_INCLUDE` too, not just a
 * definition with zero typed children — see this function's own `entities.length === 0 ||
 * hasRawEntityGroups` check below for why a typed-only partial box would be actively misleading.
 */
export function resolveBlockLocalPoints(
  name: string,
  blocks: Readonly<Record<string, BlockDefinition>>,
  resolved: Map<string, readonly Wcs3[] | typeof ALWAYS_INCLUDE>,
  inProgress: Set<string>,
  dimensionStyles?: Readonly<Record<string, DimensionStyle>>,
): readonly Wcs3[] | typeof ALWAYS_INCLUDE {
  // @codex T1 arm(c) round-24 P2 finding: own-property-safe existence check + lookup (see
  // `safeRecordLookup`'s doc) — a bare `blocks[n] !== undefined` predicate treats an INHERITED
  // `Object.prototype` member (e.g. `blocks["constructor"]`, a function) as an existing block
  // definition, so `resolveDefinitionWithCycleGuard` would proceed into `accumulate()` and read
  // that function as if it were a `BlockDefinition` — throwing or producing garbage instead of
  // the honest `ALWAYS_INCLUDE`/`null` this module's whole design promises for an unresolvable
  // block name.
  return resolveDefinitionWithCycleGuard(name, (n) => safeRecordLookup(blocks, n) !== undefined, resolved, inProgress, () => {
    const def = safeRecordLookup(blocks, name)!;
    // @codex T1 arm(c) round-18 P2 finding: a DYNAMIC block (`def.dynamicBlock === true` — the
    // canonical DXF-detected dynamism flag; `model.ts`'s `blockDefinitionSchema` doc: "Detected
    // from BLOCK_RECORD extension dictionary, XData ... or OBJECTS-section eval-graph records",
    // independent of whether the friendlier `dynamicMetadata` parse succeeded) can render
    // DIFFERENT geometry per INSERT instance (`customProperties`/`visibilityState`-driven
    // parameter/action evaluation — a real, already-integrated engine elsewhere in this codebase,
    // e.g. `apps/web/src/app/scene/blockRender.tsx`'s per-instance `evaluateBlock` cache key).
    // This module has NO evaluator access (`packages/block-evaluator` depends on
    // `drawing-model`, never the reverse — importing it here would be a circular package
    // dependency `depcruise --validate` forbids) and walking only the STATIC `def.entities` can
    // both include geometry a stretch/move action relocates AND miss geometry the same action
    // moves BEYOND its authored position — not even a safe conservative upper bound. Treating it
    // as unresolvable (mirrors the missing/cyclic-definition policy immediately below) is the
    // same "honest unknown over a misleading static-only box" policy this function's own doc
    // already establishes. Full per-instance evaluation would require injecting the evaluator at
    // an `apps/web` call site (mirroring `blockInstanceResolve.ts::specializeBlockInstance`'s
    // established pattern for the parallel `insertionOverlap.ts` bounds check) — a materially
    // larger, separate follow-up, out of scope for this bounds-honesty fix.
    if (def.dynamicBlock === true) return ALWAYS_INCLUDE;
    // Read `def.entities` exactly ONCE (round-13's own `resolvedBlockPointsCache` doc + test
    // instruments this as a live getter to COUNT accesses — reading it a second time below for the
    // empty-check, instead of reusing this array, would double-count and defeat that instrumentation).
    const entities = def.entities;
    const pts: Wcs3[] = [];
    for (const child of entities) {
      const cp = childLocalPointSet(child, blocks, resolved, inProgress, dimensionStyles);
      // An unresolvable CHILD (a nested INSERT to a missing/cyclic definition) collapses the
      // WHOLE definition to ALWAYS_INCLUDE — @codex T1 arm(c) round-11 P2 finding: the prior code
      // SKIPPED it and kept accumulating the known children, so a block with ONE unresolvable
      // nested INSERT plus otherwise-normal geometry reported a "known" `entityBounds` covering
      // only the RESOLVED portion — a real subset of the TRUE extent (the unresolved part can
      // ONLY add area, never remove it), but silently presented as the COMPLETE bounds. For a
      // QUERY/spatial-reasoning consumer (this whole module's purpose — window-select
      // pre-filtering, containment tests) an under-reported box is actively MISLEADING (a caller
      // could wrongly conclude an entity does NOT intersect a region it actually does), exactly
      // the concern `blockInsertWcsBounds`'s own doc already raises for the TOP-LEVEL
      // unresolvable-block case (round-5) — this closes the SAME gap for a NESTED unresolvable
      // child, applying the "honest unknown over a misleading partial box" policy consistently at
      // every nesting level. NOTE: this is a DELIBERATE divergence from `cad-core/
      // blockGeometry.ts::resolveDefinitionBounds`'s OWN "keep the known extent" policy (Codex
      // #467 P2) — that function serves the RENDERER (best-effort "draw what you know" is the
      // right call there), a materially different correctness bar than a bounds/query API.
      if (cp === ALWAYS_INCLUDE) return ALWAYS_INCLUDE;
      pts.push(...cp);
    }
    // `def.entities.length === 0` (NOT `pts.length === 0`) is the honest "no usable geometry"
    // signal: a definition with ZERO recognized children (all-raw/unsupported content lives in
    // `def.rawEntityGroups`, a SEPARATE field this function never walks — see this function's own
    // doc) is genuinely unknown. @codex T1 arm(c) round-21 P2 (F1/F3 fallout): a definition WITH
    // recognized children whose combined contribution nets to ZERO points is NOT "unknown" — e.g. a
    // block containing ONLY a non-constant ATTDEF now legitimately contributes nothing HERE by
    // design (F3 moves its per-instance fallback box into `resolveAttributeContribution`, which
    // `blockInsertWcsBounds`/the nested-Block case union in SEPARATELY — see `childLocalPointSet`'s
    // "Attdef" case doc). Collapsing that to `ALWAYS_INCLUDE` would have discarded the real,
    // per-instance box `resolveAttributeContribution` supplies, reporting `null` for an INSERT whose
    // bounds are actually perfectly resolvable. Checking `pts.length` here previously conflated
    // "genuinely no typed children" with "typed children legitimately summed to zero" (also already
    // true, pre-F1/F3, for a degenerate zero-point Spline/Hatch child — that latitude is unchanged
    // by this fix, since such a child's OWN empty contribution was never distinguishable from "no
    // children" under the old check either; this fix only sharpens the signal, narrowing rather than
    // widening ALWAYS_INCLUDE's trigger condition).
    //
    // @codex T1 arm(c) round-23 P2 finding: a definition with at least one RECOGNIZED (typed)
    // child is NOT automatically "fully known" either — `def.rawEntityGroups` (unsupported/raw
    // content this function never walks, preserved verbatim only for export round-trip) can be
    // non-empty on the SAME definition, e.g. a typed LINE alongside an unsupported ACAD_TABLE. The
    // prior `entities.length === 0` check alone reported ONLY the typed LINE's box for that MIXED
    // definition — a real subset of the true (unknown, possibly much larger) extent, silently
    // presented as complete, exactly the "honest unknown over a misleading partial box" concern
    // this function's own doc already establishes for an unresolvable nested INSERT (round-11) and
    // a missing/cyclic definition. `hasRawEntityGroups` closes that gap: MIXED content collapses to
    // `ALWAYS_INCLUDE` too, while a definition with ONLY typed children (empty/absent
    // `rawEntityGroups`) is unaffected — preserving round-21's own fix intent above.
    const hasRawEntityGroups = (def.rawEntityGroups?.length ?? 0) > 0;
    return entities.length === 0 || hasRawEntityGroups ? ALWAYS_INCLUDE : pts;
  });
}

/**
 * basePoint-subtract → MINSERT-grid-offset-add → scale → rotate(Z) → translate-to-insertion, for
 * ONE point. `guardInsertScale` (@codex T1 arm(c) round-19 P2 finding — F2): a schema-valid
 * zero/non-finite `scaleX`/`scaleY`/`scaleZ` renders at UNIT scale (see that helper's doc), so the
 * bounds computation must match rather than collapsing to the insertion line/plane.
 */
function transformOnePoint(
  local: Wcs3,
  gridOffset: { x: number; y: number },
  e: BlockInsertEntity,
  defBasePoint: { x: number; y: number; z: number } | undefined,
): Wcs3 {
  const bx = defBasePoint?.x ?? 0;
  const by = defBasePoint?.y ?? 0;
  const bz = defBasePoint?.z ?? 0;
  const lx = (local.x - bx + gridOffset.x) * guardInsertScale(e.scaleX);
  const ly = (local.y - by + gridOffset.y) * guardInsertScale(e.scaleY);
  const lz = (local.z - bz) * guardInsertScale(e.scaleZ);
  const r = rotateDeg2D(lx, ly, e.rotation ?? 0);
  return { x: e.insertionPoint.x + r.x, y: e.insertionPoint.y + r.y, z: (e.insertionPoint.z ?? 0) + lz };
}

/**
 * The FULL INSERT transform chain (see module doc) applied to a resolved block-definition
 * point set, tiled across the MINSERT grid, and finally transformed through the INSERT's OWN
 * `extrusion` — the shared step both a NESTED `Block` child (composing into the parent's local
 * frame) and the TOP-LEVEL `blockInsertWcsBounds` (composing into true WCS) use.
 */
function applyFullInsertChain(
  defLocalPoints: readonly Wcs3[],
  e: BlockInsertEntity,
  defBasePoint: { x: number; y: number; z: number } | undefined,
): Wcs3[] {
  const offsets = minsertGridOffsets2D(e.columnCount, e.rowCount, e.columnSpacing, e.rowSpacing);
  const axes = computeOcsAxes(e.extrusion);
  const out: Wcs3[] = [];
  for (const offset of offsets) {
    for (const p of defLocalPoints) {
      out.push(ocsToWcs(transformOnePoint(p, offset, e, defBasePoint), axes));
    }
  }
  return out;
}

/**
 * Every "blockref" `annotativeContexts[]` representation's own full transform — a synthetic
 * `BlockInsertEntity` per context, otherwise IDENTICAL to `entity` (same `blockName`/`extrusion`/
 * MINSERT grid/attributes), with ONLY `insertionPoint.x/y`, `scaleX`, `scaleY`, and `rotation`
 * substituted from the context. Mirrors the RENDERER's own resolution EXACTLY
 * (`apps/web/…/scene3d/adapters/typed/blockFromEntity.ts`'s `transform` build off
 * `resolveTypedAnnotation(insert.annotativeContexts, ctx)`: `insertion.x/y` and `scaleX`/`scaleY`/
 * `rotation` come straight from the matched context, VERBATIM — not multiplied by any further
 * annotation-scale factor — while `insertionPoint.z` and `scaleZ` stay the MASTER's own, since
 * `AnnotScaleBlockrefContext` (`annotative.ts`) carries no Z component at all: `{ insertion: {x,
 * y}, scaleX, scaleY, rotation }`).
 *
 * @codex T1 arm(c) round-24 P2 finding: `blockInsertWcsBounds` previously applied ONLY the master
 * transform, so an annotative INSERT whose matched context re-places/re-scales it far from the
 * master position was missing from `entityBounds`. Like TEXT/MTEXT's own annotative-context gap
 * (round-23 P2 finding F8, `wcsBoundsLeaf.ts::textLocal`/`mtextLocal`), this facet is a per-entity,
 * VIEWPORT-agnostic snapshot — the active CANNOSCALE the renderer resolves against is inherently
 * PER-VIEWPORT (multiple layout viewports can show the same annotative block at different active
 * scales simultaneously), so there is no single well-defined "the" active context to resolve
 * against here either. Rather than guessing one, `blockInsertWcsBounds` UNIONS the master
 * transform's footprint with EVERY context's own — the same "provably conservative, never
 * under-report" convention this module (and `wcsBoundsLeaf.ts`) already establishes.
 *
 * Codex PR #784 round 9 P2 ("Include nested blockref contexts in INSERT bounds") — EXPORTED
 * through the package barrel so `@dxf-ai-editor/cad-core`'s own INSERT placement chain
 * (`blockDefinitionWalk.ts::placeInsertInstance`, the ONE chain both a top-level INSERT and a
 * nested `Block` child go through) unions the IDENTICAL master + per-context placements instead
 * of the master transform alone. `cad-core` DEPENDS ON `drawing-model` (never the reverse), so
 * this is the correct direction for logic both packages need — the established
 * `mtextLocalCorners` / `textLocalCorners` / `mapBlockAttributeReferences` convergence precedent,
 * not a re-derivation.
 */
export function blockrefAnnotativeTransforms(entity: BlockInsertEntity): BlockInsertEntity[] {
  const out: BlockInsertEntity[] = [];
  // `?? []` — mirrors `wcsBoundsLeaf.ts::textLocal`/`mtextLocal`'s own defensive guard: the schema
  // default is `[]`, but a hand-built (non-zod-parsed) fixture/synthetic entity can omit the field.
  for (const context of entity.annotativeContexts ?? []) {
    if (context.kind !== "blockref") continue;
    out.push({
      ...entity,
      insertionPoint: { x: context.insertion.x, y: context.insertion.y, z: entity.insertionPoint.z ?? 0 },
      scaleX: context.scaleX,
      scaleY: context.scaleY,
      rotation: context.rotation,
    });
  }
  return out;
}

/**
 * Real WCS AABB of a `Block` (INSERT) entity: the resolved definition's child-geometry local
 * points transformed by the full insert chain (base-point subtract → MINSERT grid → scale →
 * rotate → OCS extrusion → translate), plus every per-instance ATTRIB (via the shared
 * `resolveAttributeContribution` — see its doc, which also covers the ORPHAN-ATTRIB fallback,
 * @codex T1 arm(c) round-10 finding):
 *   - `overridePosition` SET: a full justified text box built at that WORLD-absolute position
 *     from the matching ATTDEF's justification/widthFactor/generationFlags/obliqueAngle (@codex
 *     T1 arm(c) round-6 P2 finding-1 — previously only the bare anchor point was unioned in,
 *     under-reporting a repositioned attribute's actual rendered extent; round-10 replaced the
 *     "no matching ATTDEF ⇒ bare point" case with the renderer's orphan-fallback PLAIN style
 *     box), TILED across every MINSERT grid cell (@codex T1 arm(c) round-10 P2 finding-4 — see
 *     `tileAcrossMinsertCells`'s doc; the prior code unioned only the base cell).
 *   - `overridePosition` ABSENT: the ATTRIB follows its ATTDEF template LIVE (schema invariant),
 *     so a text box is built from the ACTUAL `attr.value` (NOT the ATTDEF's tag/default — @codex
 *     T1 arm(c) round-3 P2 finding: a real instance value can be materially wider than the
 *     generic template) at the matching ATTDEF's position/rotation/height/justification, then
 *     transformed through the SAME insert chain as the rest of the block's geometry. A tag with
 *     no matching ATTDEF (a malformed/legacy source) is the renderer's ORPHAN fallback — PLAIN
 *     style at the block origin, still riding the full insert chain (@codex T1 arm(c) round-10
 *     P2 finding-6 — previously dropped entirely).
 * `null` when the BLOCK GEOMETRY itself is unresolvable (missing/cyclic block, no usable
 * geometry, or no `blockDefinitions` context) — @codex T1 arm(c) round-5 P2 finding: a
 * per-instance ATTRIB's WORLD-absolute `overridePosition` (or its live-template box) is real,
 * independently-known WCS data, but it is NOT representative of "the entity's bounds" when the
 * (potentially much larger, unknown) block geometry itself never resolved — reporting a tiny
 * attribute-only box AS the whole entity's `entityBounds` would be MISLEADING (e.g. a window-
 * select pre-filter would wrongly conclude the block occupies only that tiny area). So an
 * unresolvable block returns `null` UNCONDITIONALLY, before any attribute is even examined —
 * never a partial, attribute-only "known" bound.
 *
 * @codex T1 arm(c) round-24 P2 finding: an annotative INSERT's `annotativeContexts[]` "blockref"
 * representations are unioned in too (see {@link blockrefAnnotativeTransforms}'s doc) — the
 * definition/attribute geometry is transformed through the FULL insert chain once per matched
 * context (in addition to the master), not just the master's own insertion/scale/rotation.
 */
export function blockInsertWcsBounds(entity: BlockInsertEntity, ctx?: BlockLookupContext): WcsAabb3 | null {
  if (!ctx?.blockDefinitions) return null;
  // @codex T1 arm(c) round-13 P2 finding: reuse the CALLER-supplied per-block-NAME memoization
  // (see `BlockLookupContext.resolvedBlockPointsCache`'s doc) across every top-level `blockInsertWcsBounds`
  // call sharing the same `blockDefinitions` table, instead of always starting from a fresh, empty
  // map. `inProgress` stays per-call (it guards the CURRENT resolution's own recursion chain, not
  // cross-call state — sharing it would falsely treat a block resolved in a PRIOR call as
  // "still being resolved" and short-circuit to `ALWAYS_INCLUDE`).
  const resolved = ctx.resolvedBlockPointsCache ?? new Map<string, readonly Wcs3[] | typeof ALWAYS_INCLUDE>();
  const inProgress = new Set<string>();
  const defPts = resolveBlockLocalPoints(entity.blockName, ctx.blockDefinitions, resolved, inProgress, ctx.dimensionStyles);
  if (defPts === ALWAYS_INCLUDE) return null; // block geometry unresolvable — honest absence, no partial attribute-only box.

  // @codex T1 arm(c) round-24 P2 finding: own-property-safe lookup (see `safeRecordLookup`'s
  // doc) — same hazard as `resolveBlockLocalPoints`'/`childLocalPointSet`'s own bare-index reads.
  const def = safeRecordLookup(ctx.blockDefinitions, entity.blockName);
  const defBasePoint = def?.basePoint;
  // @codex T1 arm(c) round-6 P2 finding-1: `overridePosition` now gets a FULL text box (via the
  // matching ATTDEF's justification/widthFactor/generationFlags/obliqueAngle), not just the bare
  // anchor point — see `resolveAttributeContribution`'s doc for the shared, nesting-safe logic
  // (round-21 F3: an unmatched variable ATTDEF's tag/"?" template fallback, same as the nested-Block
  // call site below).
  const { liveLocalPoints, overrideBypassPoints } = resolveAttributeContribution(entity.attributes, def);

  const box = emptyAabb3();
  let any = false;
  // @codex T1 arm(c) round-24 P2 finding: union the MASTER transform's footprint with EVERY
  // "blockref" annotative-context's own footprint (see `blockrefAnnotativeTransforms`'s doc) — the
  // SAME `applyFullInsertChain` call, run once per transform, so a context placed/scaled far from
  // the master position is no longer missing from `entityBounds`.
  const chainEntities = [entity, ...blockrefAnnotativeTransforms(entity)];
  for (const chainEntity of chainEntities) {
    for (const p of applyFullInsertChain([...defPts, ...liveLocalPoints], chainEntity, defBasePoint)) {
      extendAabb3(box, p);
      any = true;
    }
  }
  // @codex T1 arm(c) round-10 P2 finding: tile across this INSERT's own MINSERT grid (see
  // `tileAcrossMinsertCells`'s doc) — the prior code unioned `overrideBypassPoints` only at the
  // base cell, missing every other cell for `columnCount`/`rowCount` > 1.
  //
  // @codex T1 arm(c) round-25 P2 finding (Fix 5): tile against EVERY `chainEntity` (master +
  // every "blockref" annotative context, {@link blockrefAnnotativeTransforms}'s own doc) — not
  // just the master `entity` — since an annotative MINSERT's per-context scale/rotation changes
  // the per-cell grid offset delta too (`tileAcrossMinsertCells` reads `e.scaleX`/`scaleY`/
  // `rotation`, which DO differ per context; `columnCount`/`rowCount`/`columnSpacing`/`rowSpacing`
  // are copied verbatim by `blockrefAnnotativeTransforms`, so the grid DIMENSIONS stay identical —
  // only the per-cell WORLD delta each context's own scale/rotation produces differs). The prior
  // single call against `entity` alone reported the master's own per-cell scale for a context whose
  // real per-cell offset could be materially different, under-reporting an annotative MINSERT whose
  // matched context uses a different scale than the master.
  for (const chainEntity of chainEntities) {
    const tiledOverridePoints = tileAcrossMinsertCells(overrideBypassPoints, chainEntity, computeOcsAxes(chainEntity.extrusion));
    for (const p of tiledOverridePoints) {
      extendAabb3(box, p);
      any = true;
    }
  }

  return any && !isEmptyAabb3(box) ? box : null;
}

/**
 * An `ACAD_TABLE`'s WCS bounds — the footprint of the anonymous `*T<N>` block
 * that carries the table's rendered graphics, placed at the table's insertion
 * point and rotated by its horizontal-direction vector.
 *
 * ## Why a block reference, and why a synthetic one
 *
 * `ACAD_TABLE` literally IS a block reference in DXF: its first subclass is
 * `AcDbBlockReference`, group 2 names the `*T<N>` block and 10/20/30 is the
 * insertion point. AutoCAD draws that block's content, so its footprint is
 * the table's footprint — nothing else about the entity has an extent. The
 * `tableAsBlockReference` (drawing-model) builds that VIEW — see its own doc
 * for why it is a projection of what the DXF already says rather than a
 * fabricated entity, and why it is shared with the spatial/metadata index
 * instead of being re-derived here.
 *
 * This is exactly the technique `blockrefAnnotativeTransforms` (above)
 * already uses to reuse the insert-chain math for a non-INSERT placement.
 *
 * Returns `null` when the block table is absent or the `*T<N>` definition is
 * unresolvable — the same honest "unknown" every other block-backed kind
 * reports, never an insertion-point-only box.
 */
export function tableWcsBounds(entity: TableEntity, ctx?: BlockLookupContext): WcsAabb3 | null {
  if (!ctx?.blockDefinitions) return null;
  // `inProgress` stays per-call here for the same reason `blockInsertWcsBounds`' own does (see its
  // doc): it guards the CURRENT resolution chain, not cross-call state. A NESTED table child does
  // NOT enter through this function — `childLocalPointSet`'s `Table` arm calls
  // {@link tableLocalPointSet} directly with its caller's live guard (@codex round-5 P2 finding B).
  const resolved = ctx.resolvedBlockPointsCache ?? new Map<string, readonly Wcs3[] | typeof ALWAYS_INCLUDE>();
  const inProgress = new Set<string>();
  const pts = tableLocalPointSet(entity, ctx.blockDefinitions, resolved, inProgress, ctx.dimensionStyles);
  // TARGETED-MUTATION TRIAGE (skill `targeted-cad-mutation-testing`, "Equivalent mutant") — the two
  // survivors below are provably equivalent for THIS function's observable result, so they carry a
  // narrow, code-adjacent rationale instead of a test that cannot exist:
  //
  //   1. `ConditionalExpression` on the `pts === ALWAYS_INCLUDE` guard (→ `if (false)`).
  //      INVARIANT: `ALWAYS_INCLUDE` is the STRING `"always-include"` (`viewportMembership.ts`), so
  //      skipping the guard iterates its 13 CHARACTERS, each of which has no finite `.x/.y/.z` and
  //      is therefore dropped by `extendAabb3`'s non-finite guard — the accumulator stays at its
  //      `emptyAabb3()` identity and the function still returns `null`. The guard is kept because it
  //      states the contract (and because `tableLocalPointSet`'s OWN identical early return is NOT
  //      equivalent — it feeds the nested-child arm, where the sentinel must propagate; that one is
  //      killed by the "UNRESOLVABLE `*T<N>` collapses the WHOLE enclosing definition" test).
  //   2. `BooleanLiteral` on `let any = false` (→ `let any = true`).
  //      INVARIANT: `any` is read ONLY in the final conjunction, and `!isEmptyAabb3(box)` is already
  //      false whenever the loop added nothing — `false && false` and `true && false` both yield
  //      `null`. The flag is kept for symmetry with `blockInsertWcsBounds`' identical accumulator.
  //
  // Every OTHER mutant in this function and in `tableLocalPointSet` IS killed — see
  // `wcsBounds.blockTable.test.ts`'s no-context / missing-definition / non-finite-insertion-point /
  // stale-shared-cache cases, each written for exactly one of them.
  if (pts === ALWAYS_INCLUDE) return null;

  const box = emptyAabb3();
  let any = false;
  for (const p of pts) {
    extendAabb3(box, p);
    any = true;
  }
  return any && !isEmptyAabb3(box) ? box : null;
}

/**
 * An `ACAD_TABLE`'s resolved point set in the frame the table entity itself lives in — the
 * `*T<N>` graphics block's own local points pushed through the table's synthetic block-reference
 * placement ({@link tableAsBlockReference} + the shared `applyFullInsertChain`).
 *
 * ONE truth for both table call sites: {@link tableWcsBounds} boxes this set for a TOP-LEVEL
 * table, and `childLocalPointSet`'s `Table` arm returns it VERBATIM for a block-child table so
 * the enclosing INSERT chain transforms every real point.
 *
 * ## Why a POINT SET and not an AABB (@codex round-5 P2 finding A)
 *
 * A block-child table's points take a further, a-priori-unknown rotation from every enclosing
 * INSERT. An axis-aligned box is closed only under axis-aligned maps, so handing the caller a box
 * (or its two diagonal corners) loses the cross-corners that attain the rotated extrema. Returning
 * the resolved points — rather than re-enumerating four corners of a box that already threw the
 * real points away — is both exact and cheaper here: `tableWcsBounds` was already computing this
 * set and immediately collapsing it, so no extra work is introduced at either site, and the Z
 * extrema ride along for free instead of needing a separate 8-corner expansion.
 *
 * ## Why the cycle guard is a PARAMETER (@codex round-5 P2 finding B)
 *
 * `ACAD_TABLE` is a block reference, so a table is a real edge in the block-reference graph and a
 * cycle can run through it (block `A` holds a table whose `*T<N>` block holds an INSERT of `A`).
 * The caller's live `inProgress` set must therefore cross the table boundary exactly as it crosses
 * a nested `Block` boundary; a fresh set at this boundary makes the whole chain invisible to
 * `resolveDefinitionWithCycleGuard` and recursion never terminates. With the guard threaded, the
 * cycle resolves to `ALWAYS_INCLUDE` — this module's documented "unknown extent" answer, which
 * `tableWcsBounds`/`blockInsertWcsBounds` surface as `null`.
 */
function tableLocalPointSet(
  entity: TableEntity,
  blocks: Readonly<Record<string, BlockDefinition>>,
  resolved: Map<string, readonly Wcs3[] | typeof ALWAYS_INCLUDE>,
  inProgress: Set<string>,
  dimensionStyles: Readonly<Record<string, DimensionStyle>> | undefined,
): Wcs3[] | typeof ALWAYS_INCLUDE {
  const defPts = resolveBlockLocalPoints(entity.blockName, blocks, resolved, inProgress, dimensionStyles);
  if (defPts === ALWAYS_INCLUDE) return ALWAYS_INCLUDE;
  const def = safeRecordLookup(blocks, entity.blockName);
  return applyFullInsertChain(defPts, tableAsBlockReference(entity), def?.basePoint);
}
